
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axiosInstance from '../../services/api/axiosInstance';

const UserContext = createContext();

export const UserProvider = ({ children }) => {

  // ✅ Reads localStorage synchronously — user is available on frame 1
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  });

  // True while AuthInitializer validates a saved token. validate-token rotates the token,
  // so authenticated calls must wait or they race with the old token and get a 401.
  const [isInitializing, setIsInitializing] = useState(() => !!localStorage.getItem('token'));
  const [platformAccess, setPlatformAccess] = useState(null);
  const [platformAccessError, setPlatformAccessError] = useState('');
  const [platformAccessLoading, setPlatformAccessLoading] = useState(Boolean(user));

  const refreshPlatformAccess = useCallback(async () => {
    if (!user) {
      setPlatformAccess(null);
      setPlatformAccessError('');
      return null;
    }
    setPlatformAccessLoading(true);
    try {
      const response = await axiosInstance.get('/platform-access/me');
      const payload = response?.data?.data ?? response?.data;
      setPlatformAccess(payload);
      setPlatformAccessError('');
      return payload;
    } catch (error) {
      setPlatformAccess(null);
      setPlatformAccessError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unable to load platform access.'
      );
      return null;
    } finally {
      setPlatformAccessLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isInitializing) return;
    if (user) refreshPlatformAccess();
    else {
      setPlatformAccess(null);
      setPlatformAccessError('');
      setPlatformAccessLoading(false);
    }
  }, [user, isInitializing, refreshPlatformAccess]);

  const login = useCallback((userData) => {
    setPlatformAccessLoading(true);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    window.dispatchEvent(new Event('auth:logout'));
    setUser(null);
    setPlatformAccess(null);
    setPlatformAccessError('');
    setPlatformAccessLoading(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('auth_role');
  }, []);

  const setInitialized = useCallback(() => setIsInitializing(false), []);
  const updateUser = useCallback((patch) => {
    setUser(prev => {
      if (!prev) return prev;
      // merge into the nested `user` object if that's where these flags live
      const next = {
        ...prev,
        user: { ...prev.user, ...patch },
      };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);
  return (
    <UserContext.Provider value={{
      user,
      login,
      logout,
      isInitializing,
      setInitialized,
      updateUser,
      platformAccess,
      platformAccessError,
      platformAccessLoading,
      refreshPlatformAccess,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};